
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Eye, Filter, TrendingUp } from 'lucide-react';
import { mockTerraces } from '@/data/mockTerraces';
import useAuth from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { DashboardCard } from './widgets';
import { Skeleton } from '@/components/ui/skeleton';

const HistoryTab = () => {
  const { user, profile } = useAuth();
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!user || !profile) return;
    
    const fetchRecentlyViewed = async () => {
      try {
        const { data, error } = await supabase
          .from('recently_viewed')
          .select('terrace_id, viewed_at')
          .eq('user_id', user.id)
          .order('viewed_at', { ascending: false })
          .limit(10);
          
        if (error) throw error;
        
        // Map the recently viewed terrace ids to actual terrace data
        const terraceData = data.map(item => {
          const terrace = mockTerraces.find(t => t.id === item.terrace_id);
          return {
            ...terrace,
            viewed_at: item.viewed_at
          };
        }).filter(Boolean);
        
        setRecentlyViewed(terraceData);
      } catch (error) {
        console.error('Error fetching recently viewed:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentlyViewed();
  }, [user, profile]);

  if (!user || !profile) return null;

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-sunset-yellow/10 rounded-lg">
            <Clock className="h-6 w-6 text-sunset-yellow" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Recent Activity</h1>
            <p className="text-muted-foreground">
              Terraces you've recently explored
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-sunset-yellow/10 text-sunset-dark border-sunset-yellow/30">
            {recentlyViewed.length} recent
          </Badge>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-1" />
            Filter
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-32 w-full" />
              <CardContent className="p-4">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2 mb-4" />
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (recentlyViewed.length === 0) ? (
        <DashboardCard
          title="No Recent Activity"
          description="Start exploring to see your browsing history"
          icon={<Clock className="h-5 w-5" />}
          variant="default"
          isPlaceholder
          placeholderText="History Tracking Coming"
        >
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-sunset-yellow/10 to-sunset-yellow/5 mb-4">
              <TrendingUp className="h-8 w-8 text-sunset-yellow" />
            </div>
            <h3 className="text-lg font-medium mb-2">Begin Your Journey</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Explore Stockholm's terraces and we'll keep track of your favorites for quick revisits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/list">
                  <Eye className="h-4 w-4 mr-2" />
                  Start Exploring
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/">
                  <Clock className="h-4 w-4 mr-2" />
                  View Map
                </Link>
              </Button>
            </div>
          </div>
        </DashboardCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {recentlyViewed.map(terrace => (
            <Card key={terrace.id} className="group overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-sunset-yellow/10 border-sunset-yellow/20">
              <div className="relative h-32 overflow-hidden">
                <img 
                  src={terrace.photo_url || 'https://images.unsplash.com/photo-1500673922987-e212871fec22'}
                  alt={terrace.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="bg-white/90 text-xs">
                    <Clock className="h-3 w-3 mr-1" /> 
                    {terrace.viewed_at ? formatDistanceToNow(new Date(terrace.viewed_at), { addSuffix: true }) : ''}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold group-hover:text-sunset-yellow transition-colors">
                      {terrace.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{terrace.type}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/terrace/${terrace.id}`}>
                      <Eye className="h-4 w-4 mr-1" />
                      Visit Again
                    </Link>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="px-3 text-sunset-yellow hover:text-sunset-yellow hover:bg-sunset-yellow/10"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
